import { useState, useEffect, useCallback } from 'react';
import { fetchMonthlyExpenses, createMonthlyExpense, updateMonthlyExpense, deleteMonthlyExpense, fetchFlujoCaja, fetchMonthStatuses, setMonthStatus, fetchCostProjectionMonth, fetchLiberacionesDetalle, fetchIngresosViviendasDetalle } from '@/lib/api';
import { formatMXN, formatMXNCompact } from '@/lib/formatters';
import { useAuth } from '@/lib/AuthContext';
import { PageHeader } from '@/components/common/PageHeader';
import { EmptyState } from '@/components/common/EmptyState';
import { KPICard } from '@/components/common/KPICard';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Plus, Trash2, Receipt, TrendingDown, TrendingUp, Wallet, Pencil, AlertTriangle, CheckCircle2 } from 'lucide-react';

const MES_NAMES = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
const YEARS = [2025, 2026, 2027, 2028];

const EXPENSE_CATS = [
  { key: 'materials', label: 'Materiales' },
  { key: 'labor', label: 'Mano de Obra' },
  { key: 'equipment', label: 'Herramienta y Equipo' },
  { key: 'admin', label: 'Gastos de Administración' },
  { key: 'sales_commissions', label: 'Comisiones de Ventas' },
  { key: 'taxes', label: 'Impuestos' },
  { key: 'imss', label: 'IMSS' },
  { key: 'lot_payments', label: 'Liquidación de Lotes' },
  { key: 'other_expenses', label: 'Otros Gastos' },
];

const INCOME_CATS = [
  { key: 'capital', label: 'Capital' },
  { key: 'urbanization_income', label: 'Ingresos por Urbanización' },
  { key: 'other_income', label: 'Otros Ingresos' },
];

const emptyForm = () => {
  const f = { year: new Date().getFullYear(), month: new Date().getMonth() + 1, intereses_override: '', actual_intereses: '', actual_prestamos: '' };
  EXPENSE_CATS.forEach(c => { f[`projected_${c.key}`] = ''; f[`actual_${c.key}`] = ''; });
  INCOME_CATS.forEach(c => { f[`projected_${c.key}`] = ''; f[`actual_${c.key}`] = ''; });
  return f;
};

export default function Egresos() {
  const { can } = useAuth();
  const isAdmin = can('egresos', 'admin');
  const [expenses, setExpenses] = useState([]);
  const [flujoByMonth, setFlujoByMonth] = useState({});
  const [monthStatuses, setMonthStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const load = useCallback(async () => {
    try {
      const [data, flujo, ms] = await Promise.all([fetchMonthlyExpenses(), fetchFlujoCaja(), fetchMonthStatuses().catch(() => ({}))]);
      data.sort((a, b) => a.year === b.year ? a.month - b.month : a.year - b.year);
      setExpenses(data);
      // Build flujo lookup by "YYYY-MM"
      const lookup = {};
      for (const m of (flujo.meses || [])) {
        lookup[m.mes] = {
          proy_intereses: m.proy_intereses || 0,
          real_intereses: m.real_intereses || 0,
          proy_prestamos: m.proy_prestamos || 0,
          proy_liberaciones: m.proy_liberaciones || 0,
          real_liberaciones: m.real_liberaciones || 0,
          proy_ingresos: m.proy_ingresos || 0,
          real_ingresos: m.real_ingresos || 0,
          proy_egresos: m.proy_egresos || 0,
          real_egresos: m.real_egresos || 0,
        };
      }
      setFlujoByMonth(lookup);
      setMonthStatuses(ms);
    } catch { toast.error('Error al cargar'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  const handleSaved = () => { setDlgOpen(false); setEditTarget(null); load(); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    try { await deleteMonthlyExpense(deleteTarget.id); toast.success('Eliminado'); load(); }
    catch { toast.error('Error'); } finally { setDeleteTarget(null); }
  };

  const handleMonthStatus = async (mes, newStatus) => {
    try {
      await setMonthStatus(mes, newStatus);
      setMonthStatuses(prev => ({ ...prev, [mes]: newStatus }));
      toast.success(`${mes} → ${newStatus === 'validado' ? 'Validado ✓' : newStatus === 'cerrado' ? 'Cerrado 🔒' : 'Abierto'}`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
  };

  const totals = expenses.reduce((acc, e) => ({
    proyEg: acc.proyEg + (e.total_projected_expenses || 0),
    realEg: acc.realEg + (e.total_actual_expenses || 0),
    proyIng: acc.proyIng + (e.total_projected_income || 0),
    realIng: acc.realIng + (e.total_actual_income || 0),
  }), { proyEg: 0, realEg: 0, proyIng: 0, realIng: 0 });

  // Flujo totals (include property payments + capital = full income picture)
  const flujoTotals = Object.values(flujoByMonth).reduce((acc, m) => ({
    proyIng: acc.proyIng + (m.proy_ingresos || 0),
    realIng: acc.realIng + (m.real_ingresos || 0),
    proyEg: acc.proyEg + (m.proy_egresos || 0),
    realEg: acc.realEg + (m.real_egresos || 0),
  }), { proyIng: 0, realIng: 0, proyEg: 0, realEg: 0 });

  // Period label from data
  const MES_SHORT = ['','Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  const sortedExpenses = [...expenses].sort((a, b) => a.year === b.year ? a.month - b.month : a.year - b.year);
  const periodoLabel = sortedExpenses.length > 0
    ? `${MES_SHORT[sortedExpenses[0].month]} - ${MES_SHORT[sortedExpenses[sortedExpenses.length - 1].month]} ${sortedExpenses[sortedExpenses.length - 1].year}`
    : '';

  if (loading) return (
    <div className="space-y-6"><Skeleton className="h-10 w-64" /><div className="grid grid-cols-2 md:grid-cols-4 gap-4">{[1,2,3,4].map(i => <Skeleton key={i} className="h-28 rounded-xl" />)}</div><Skeleton className="h-[400px] rounded-xl" /></div>
  );

  return (
    <div className="space-y-6" data-testid="egresos-page">
      <PageHeader title="Egresos" subtitle={`${expenses.length} meses registrados`}>
        <Button onClick={() => { setEditTarget(null); setDlgOpen(true); }} data-testid="add-expense-btn"><Plus className="h-4 w-4 mr-1.5" /> Nuevo Mes</Button>
      </PageHeader>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KPICard label="Eg. Proyectados" value={formatMXNCompact(flujoTotals.proyEg)} subtitle={`Total · ${periodoLabel}`} icon={TrendingDown} compact testId="kpi-eg-proy" />
        <KPICard label="Eg. Reales" value={formatMXNCompact(flujoTotals.realEg)} subtitle={`Total · ${periodoLabel}`} icon={TrendingDown} compact testId="kpi-eg-real" />
        <KPICard label="Ing. Proyectados" value={formatMXNCompact(flujoTotals.proyIng)} subtitle={`Ventas + Capital · ${periodoLabel}`} icon={TrendingUp} compact testId="kpi-ing-proy" />
        <KPICard label="Ing. Reales" value={formatMXNCompact(flujoTotals.realIng)} subtitle={`Ventas + Capital · ${periodoLabel}`} icon={TrendingUp} compact testId="kpi-ing-real" />
      </div>

      {expenses.length === 0 ? (
        <EmptyState title="Sin registros" description="Agrega el primer mes." icon={Receipt} />
      ) : (
        <Card className="rounded-xl border bg-white overflow-clip">
            <Table data-testid="egresos-table">
              <TableHeader className="sticky top-0 z-20">
                <TableRow className="bg-slate-50 shadow-[0_1px_0_0_rgb(226,232,240)]">
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-2.5 w-24 sticky left-0 bg-slate-50 z-10">Mes</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-emerald-600 font-semibold py-3 px-2.5 text-right">Ing. Ventas Proy.</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-3 px-2.5 text-right">Ing. Ventas Real</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-2.5 text-right">Capital Proy.</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-3 px-2.5 text-right">Capital Real</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-2.5 text-right">Eg. Op. Proy.</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-3 px-2.5 text-right">Eg. Op. Real</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-2.5 text-right">Int. Proy.</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-[#F05A28] font-semibold py-3 px-2.5 text-right">Int. Real</TableHead>
                  <TableHead className="text-[9px] uppercase tracking-widest text-slate-400 font-semibold py-3 px-2.5 text-right">Prést. Proy.</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {expenses.map(e => {
                  const mesKey = `${e.year}-${String(e.month).padStart(2, '0')}`;
                  const fm = flujoByMonth[mesKey] || {};
                  const st = monthStatuses[mesKey] || 'abierto';
                  const isValidated = st === 'validado';
                  const nextStatus = isValidated ? 'abierto' : 'validado';
                  // Income from sales = flujo total income - capital income
                  const ventasProy = (fm.proy_ingresos || 0) - (e.total_projected_income || 0);
                  const ventasReal = (fm.real_ingresos || 0) - (e.total_actual_income || 0);

                  const rowBg = isValidated ? 'bg-emerald-50/40' : '';

                  return (
                    <TableRow key={e.id} className={`group ${rowBg}`} data-testid={`expense-row-${e.id}`}>
                      <TableCell className={`py-2.5 px-2.5 sticky left-0 z-10 ${isValidated ? 'bg-emerald-50/40' : 'bg-white'}`}>
                        <div className="flex items-center gap-1.5">
                          <span className="text-[12px] font-semibold">{e.mes_label}</span>
                          {isValidated && <CheckCircle2 className="h-3 w-3 text-emerald-500" />}
                        </div>
                      </TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums text-emerald-700">{ventasProy > 0 ? formatMXN(ventasProy) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{ventasReal > 0 ? formatMXN(ventasReal) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums">{(e.total_projected_income || 0) > 0 ? formatMXN(e.total_projected_income) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums text-[#F05A28]">{(e.total_actual_income || 0) > 0 ? formatMXN(e.total_actual_income) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums">{formatMXN(e.total_projected_expenses)}</TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{(e.total_actual_expenses || 0) > 0 ? formatMXN(e.total_actual_expenses) : <span className="text-slate-300">$0</span>}</TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[10px] tabular-nums text-slate-400">{formatMXN(fm.proy_intereses || 0)}</TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[10px] tabular-nums text-[#F05A28]">{(fm.real_intereses || 0) > 0 ? formatMXN(fm.real_intereses) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-2.5 text-right text-[10px] tabular-nums text-slate-400">{(fm.proy_prestamos || 0) > 0 ? formatMXN(fm.proy_prestamos) : <span className="text-slate-300">—</span>}</TableCell>
                      <TableCell className="py-2.5 px-2.5">
                        <div className="flex items-center gap-0.5">
                          <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-slate-700" onClick={() => { setEditTarget(e); setDlgOpen(true); }}><Pencil className="h-3 w-3" /></Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-red-500" onClick={() => setDeleteTarget(e)}><Trash2 className="h-3 w-3" /></Button>
                          </div>
                          {isAdmin && (
                            <button
                              onClick={() => handleMonthStatus(mesKey, nextStatus)}
                              className={`ml-auto px-2 py-1 rounded-full text-[8px] font-semibold transition-colors whitespace-nowrap ${
                                isValidated ? 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200' :
                                'bg-white text-slate-400 hover:bg-slate-100 border border-slate-200'
                              }`}
                              title={isValidated ? 'Regresar a Abierto' : 'Validar mes (forecast usará datos reales)'}
                            >
                              {isValidated ? '✓ Validado' : 'Validar'}
                            </button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="bg-slate-50 font-semibold border-t">
                  <TableCell className="py-2.5 px-2.5 text-[11px] uppercase tracking-wider text-slate-500 sticky left-0 bg-slate-50 z-10">Total</TableCell>
                  <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums font-bold text-emerald-700">{formatMXN(flujoTotals.proyIng - totals.proyIng)}</TableCell>
                  <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{formatMXN(flujoTotals.realIng - totals.realIng)}</TableCell>
                  <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums font-bold">{formatMXN(totals.proyIng)}</TableCell>
                  <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{formatMXN(totals.realIng)}</TableCell>
                  <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums font-bold">{formatMXN(totals.proyEg)}</TableCell>
                  <TableCell className="py-2.5 px-2.5 text-right text-[11px] tabular-nums font-bold text-[#F05A28]">{formatMXN(totals.realEg)}</TableCell>
                  <TableCell colSpan={3} />
                </TableRow>
              </TableBody>
            </Table>
        </Card>
      )}

      <ExpenseDialog open={dlgOpen} onClose={() => { setDlgOpen(false); setEditTarget(null); }} onSaved={handleSaved} initial={editTarget} flujoByMonth={flujoByMonth} />

      <AlertDialog open={!!deleteTarget} onOpenChange={v => { if (!v) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader><AlertDialogTitle>Eliminar registro</AlertDialogTitle><AlertDialogDescription>Se eliminará {deleteTarget?.mes_label}.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white">Eliminar</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function ExpenseDialog({ open, onClose, onSaved, initial, flujoByMonth }) {
  const isEdit = !!initial;
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [projData, setProjData] = useState(null);
  const [loadingProj, setLoadingProj] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) {
      if (initial) {
        const f = { year: initial.year, month: initial.month, intereses_override: initial.intereses_override ?? '', actual_intereses: initial.actual_intereses ?? '', actual_prestamos: initial.actual_prestamos ?? '' };
        EXPENSE_CATS.forEach(c => { f[`projected_${c.key}`] = initial[`projected_${c.key}`] || ''; f[`actual_${c.key}`] = initial[`actual_${c.key}`] || ''; });
        INCOME_CATS.forEach(c => { f[`projected_${c.key}`] = initial[`projected_${c.key}`] || ''; f[`actual_${c.key}`] = initial[`actual_${c.key}`] || ''; });
        setForm(f);
      } else { setForm(emptyForm()); }
    }
  }, [open, initial]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const data = { year: parseInt(form.year), month: parseInt(form.month), actual_intereses: form.actual_intereses !== '' ? parseFloat(form.actual_intereses) : null, actual_prestamos: form.actual_prestamos !== '' ? parseFloat(form.actual_prestamos) : null };
      EXPENSE_CATS.forEach(c => {
        const pv = form[`projected_${c.key}`];
        const av = form[`actual_${c.key}`];
        data[`projected_${c.key}`] = pv !== '' && pv !== null && pv !== undefined ? parseFloat(pv) || 0 : 0;
        data[`actual_${c.key}`] = av !== '' && av !== null && av !== undefined ? parseFloat(av) || 0 : 0;
      });
      INCOME_CATS.forEach(c => {
        const pv = form[`projected_${c.key}`];
        const av = form[`actual_${c.key}`];
        data[`projected_${c.key}`] = pv !== '' && pv !== null && pv !== undefined ? parseFloat(pv) || 0 : 0;
        data[`actual_${c.key}`] = av !== '' && av !== null && av !== undefined ? parseFloat(av) || 0 : 0;
      });
      if (isEdit) { 
        const result = await updateMonthlyExpense(initial.id, data); 
        toast.success(`Actualizado: Mat Proy ${formatMXN(data.projected_materials || 0)} | MO Proy ${formatMXN(data.projected_labor || 0)}`); 
      }
      else { await createMonthlyExpense(data); toast.success('Creado'); }
      onSaved();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); }
    finally { setSaving(false); }
  };

  const totalProyEg = EXPENSE_CATS.reduce((s, c) => s + (parseFloat(form[`projected_${c.key}`]) || 0), 0);
  const totalRealEg = EXPENSE_CATS.reduce((s, c) => s + (parseFloat(form[`actual_${c.key}`]) || 0), 0);
  const totalProyIng = INCOME_CATS.reduce((s, c) => s + (parseFloat(form[`projected_${c.key}`]) || 0), 0);
  const totalRealIng = INCOME_CATS.reduce((s, c) => s + (parseFloat(form[`actual_${c.key}`]) || 0), 0);

  // Get auto-calculated values from flujo engine for this month
  const mesKey = `${form.year}-${String(form.month).padStart(2, '0')}`;
  const flujoMes = (flujoByMonth || {})[mesKey] || {};
  const autoIntereses = flujoMes.proy_intereses || 0;
  const autoPrestamos = flujoMes.proy_prestamos || 0;
  const autoLiberaciones = flujoMes.proy_liberaciones || 0;
  const [liberDetail, setLiberDetail] = useState(null);
  const [ingresosDetail, setIngresosDetail] = useState(null);
  const autoRealLiberaciones = liberDetail?.total_real || 0;

  // Auto-load liberaciones detail when dialog opens for a specific month
  useEffect(() => {
    if (open && initial?.year && initial?.month) {
      const mk = `${initial.year}-${String(initial.month).padStart(2, '0')}`;
      fetchLiberacionesDetalle(mk).then(setLiberDetail).catch(() => setLiberDetail(null));
      fetchIngresosViviendasDetalle(mk).then(setIngresosDetail).catch(() => setIngresosDetail(null));
    } else if (open && !initial) {
      setLiberDetail(null);
      setIngresosDetail(null);
    }
  }, [open, initial]);

  return (
    <Dialog open={open} onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto" data-testid="expense-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-[#F05A28]" />{isEdit ? `Editar — ${MES_NAMES[form.month]} ${form.year}` : 'Nuevo Mes'}</DialogTitle></DialogHeader>
        <div className="space-y-5 py-2">
          {/* Month/Year */}
          {!isEdit && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Año</Label><Select value={String(form.year)} onValueChange={v => set('year', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{YEARS.map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="text-xs">Mes</Label><Select value={String(form.month)} onValueChange={v => set('month', v)}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MES_NAMES.slice(1).map((m, i) => <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>)}</SelectContent></Select></div>
            </div>
          )}

          {/* ── EGRESOS ── */}
          <div className="flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Egresos</p>
            <Button variant="outline" size="sm" className="text-[10px] h-7" disabled={loadingProj} onClick={async () => {
              setLoadingProj(true);
              try {
                const mesKey = `${form.year}-${String(form.month).padStart(2, '0')}`;
                const proj = await fetchCostProjectionMonth(mesKey);
                setProjData(proj);
                if (proj.materials > 0) set('projected_materials', String(Math.round(proj.materials)));
                if (proj.labor > 0) set('projected_labor', String(Math.round(proj.labor)));
                toast.success(`Materiales: ${formatMXN(proj.materials)} | Mano de obra: ${formatMXN(proj.labor)}`);
              } catch { toast.error('Error al calcular'); }
              finally { setLoadingProj(false); }
            }}>
              {loadingProj ? 'Calculando...' : 'Calcular desde Viviendas'}
            </Button>
          </div>
          {projData && projData.properties && projData.properties.length > 0 && (
            <details className="text-xs">
              <summary className="cursor-pointer text-[10px] text-blue-600 hover:text-blue-800">Ver desglose por vivienda ({projData.properties.length})</summary>
              <div className="mt-2 bg-blue-50/50 border border-blue-100 rounded-lg p-2.5 space-y-0.5 max-h-[120px] overflow-y-auto">
                {projData.properties.map((p, i) => (
                  <div key={i} className="flex justify-between text-[10px]">
                    <span className="text-blue-700">Lt {p.lot} Mz {p.block} <span className="text-blue-400">({p.months_remaining}m)</span></span>
                    <span className="tabular-nums text-blue-700">{formatMXN(p.materials + p.labor)}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
          <DualSection title="" categories={EXPENSE_CATS} form={form} set={set} totalProy={totalProyEg} totalReal={totalRealEg} hideTotal />

          {/* ── INTERESES Y PRÉSTAMOS ── */}
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Intereses de Crédito y Préstamos</p>
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 mb-2">
              <span />
              <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 text-center">Proyectado</span>
              <span className="text-[9px] font-semibold uppercase tracking-widest text-[#F05A28] text-center">Real</span>
            </div>
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 items-center mb-2">
              <Label className="text-xs text-slate-600">Intereses</Label>
              <div className="h-8 flex items-center justify-end px-3 bg-slate-50 rounded-md border border-slate-200 text-xs tabular-nums text-slate-600 font-medium">{formatMXN(autoIntereses)}</div>
              <Input type="number" step="0.01" className="h-8 text-xs text-right" placeholder="No capturado" value={form.actual_intereses} onChange={e => set('actual_intereses', e.target.value)} />
            </div>
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 items-center mb-2">
              <Label className="text-xs text-slate-600">Préstamos</Label>
              <div className="h-8 flex items-center justify-end px-3 bg-slate-50 rounded-md border border-slate-200 text-xs tabular-nums text-slate-600 font-medium">{formatMXN(autoPrestamos)}</div>
              <Input type="number" step="0.01" className="h-8 text-xs text-right" placeholder="No capturado" value={form.actual_prestamos} onChange={e => set('actual_prestamos', e.target.value)} />
            </div>
            {(autoLiberaciones > 0 || autoRealLiberaciones > 0 || (liberDetail && (liberDetail.total_proyectado > 0 || liberDetail.total_real > 0))) && (
              <>
                <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 items-center mb-2">
                  <Label className="text-xs text-slate-600">Liberaciones</Label>
                  <div className="h-8 flex items-center justify-end px-3 bg-slate-50 rounded-md border border-slate-200 text-xs tabular-nums text-slate-600 font-medium">{formatMXN(liberDetail?.total_proyectado || autoLiberaciones)}</div>
                  <div className={`h-8 flex items-center justify-end px-3 rounded-md border text-xs tabular-nums font-medium ${autoRealLiberaciones > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>{formatMXN(autoRealLiberaciones)}</div>
                </div>
                {liberDetail && (liberDetail.proyectadas?.length > 0 || liberDetail.reales?.length > 0) && (
                  <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-2 space-y-2 max-h-[140px] overflow-y-auto">
                    {liberDetail.proyectadas?.length > 0 && (
                      <div>
                        <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Proyectadas ({liberDetail.proyectadas.length})</p>
                        {liberDetail.proyectadas.map((l, i) => (
                          <div key={i} className="flex justify-between text-[10px]"><span className="text-slate-600">Lt {l.lot} Mz {l.block} — {l.name}</span><span className="tabular-nums font-medium">{formatMXN(l.amount)}</span></div>
                        ))}
                      </div>
                    )}
                    {liberDetail.reales?.length > 0 && (
                      <div>
                        <p className="text-[9px] font-semibold uppercase tracking-widest text-emerald-600 mb-1">Reales ({liberDetail.reales.length})</p>
                        {liberDetail.reales.map((l, i) => (
                          <div key={i} className="flex justify-between text-[10px]"><span className="text-emerald-700">Lt {l.lot} Mz {l.block} — {l.name} <span className="text-emerald-400">({l.date})</span></span><span className="tabular-nums font-medium text-emerald-700">{formatMXN(l.amount)}</span></div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
            <p className="text-[9px] text-slate-400 mt-1">Proyectado: calculado automáticamente desde el crédito y los préstamos registrados.</p>
            {/* Total egresos */}
            <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 items-center pt-3 border-t border-slate-300 mt-3">
              <span className="text-xs font-bold text-slate-800">Total Egresos</span>
              <div className="text-xs font-bold tabular-nums text-center">{formatMXN(totalProyEg + autoIntereses + autoPrestamos + autoLiberaciones)}</div>
              <span className="text-xs font-bold tabular-nums text-center text-[#F05A28]">{formatMXN(totalRealEg + (parseFloat(form.actual_intereses) || 0) + (parseFloat(form.actual_prestamos) || 0) + autoRealLiberaciones)}</span>
            </div>
          </div>

          {/* ── INGRESOS ── */}
          <DualSection title="Ingresos (otros)" categories={INCOME_CATS} form={form} set={set} totalProy={totalProyIng} totalReal={totalRealIng} />

          {/* ── INGRESOS POR VIVIENDAS (auto) ── */}
          {ingresosDetail && (ingresosDetail.total_proyectado > 0 || ingresosDetail.total_real > 0) && (
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">Ingresos por Viviendas (automático)</p>
              <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 mb-2">
                <span />
                <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 text-center">Proyectado</span>
                <span className="text-[9px] font-semibold uppercase tracking-widest text-[#F05A28] text-center">Real</span>
              </div>
              <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 items-center mb-2">
                <Label className="text-xs text-slate-600">Cobros de viviendas</Label>
                <div className="h-8 flex items-center justify-end px-3 bg-slate-50 rounded-md border border-slate-200 text-xs tabular-nums text-slate-600 font-medium">{formatMXN(ingresosDetail.total_proyectado)}</div>
                <div className={`h-8 flex items-center justify-end px-3 rounded-md border text-xs tabular-nums font-medium ${ingresosDetail.total_real > 0 ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-slate-50 border-slate-200 text-slate-400'}`}>{formatMXN(ingresosDetail.total_real)}</div>
              </div>
              {(ingresosDetail.proyectados?.length > 0 || ingresosDetail.reales?.length > 0) && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-2 space-y-2 max-h-[160px] overflow-y-auto">
                  {ingresosDetail.proyectados?.length > 0 && (
                    <div>
                      <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 mb-1">Proyectados ({ingresosDetail.proyectados.length} viviendas)</p>
                      {ingresosDetail.proyectados.map((p, i) => (
                        <div key={i} className="flex justify-between text-[10px]">
                          <span className="text-slate-600">Lt {p.lot} Mz {p.block} — {p.name}</span>
                          <span className="tabular-nums font-medium">{formatMXN(p.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {ingresosDetail.reales?.length > 0 && (
                    <div>
                      <p className="text-[9px] font-semibold uppercase tracking-widest text-emerald-600 mb-1">Reales ({ingresosDetail.reales.length} viviendas)</p>
                      {ingresosDetail.reales.map((p, i) => (
                        <div key={i} className="flex justify-between text-[10px]">
                          <span className="text-emerald-700">Lt {p.lot} Mz {p.block} — {p.name}</span>
                          <span className="tabular-nums font-medium text-emerald-700">{formatMXN(p.total)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              <p className="text-[9px] text-slate-400">Calculado automáticamente desde los pagos y abonos de viviendas.</p>
            </div>
          )}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="outline">Cancelar</Button></DialogClose>
          <Button onClick={handleSave} disabled={saving} data-testid="expense-save-btn">{saving ? 'Guardando...' : isEdit ? 'Guardar Cambios' : 'Crear Registro'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ── Reusable dual-column section ── */
function DualSection({ title, categories, form, set, totalProy, totalReal, hideTotal }) {
  return (
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-400 mb-3">{title}</p>
      <DualHeader />
      {categories.map(c => (
        <div key={c.key} className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 items-center mb-2">
          <Label className="text-xs text-slate-600">{c.label}</Label>
          <Input type="number" step="0.01" className="h-8 text-xs text-right" placeholder="0" value={form[`projected_${c.key}`]} onChange={e => set(`projected_${c.key}`, e.target.value)} />
          <Input type="number" step="0.01" className="h-8 text-xs text-right" placeholder="0" value={form[`actual_${c.key}`]} onChange={e => set(`actual_${c.key}`, e.target.value)} />
        </div>
      ))}
      {!hideTotal && (
        <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 items-center pt-2 border-t border-slate-200 mt-1">
          <span className="text-xs font-bold text-slate-700">Total</span>
          <span className="text-xs font-bold tabular-nums text-center">{formatMXN(totalProy)}</span>
          <span className="text-xs font-bold tabular-nums text-center text-[#F05A28]">{formatMXN(totalReal)}</span>
        </div>
      )}
    </div>
  );
}

function DualHeader() {
  return (
    <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 mb-2">
      <span />
      <span className="text-[9px] font-semibold uppercase tracking-widest text-slate-400 text-center">Proyectado</span>
      <span className="text-[9px] font-semibold uppercase tracking-widest text-[#F05A28] text-center">Real</span>
    </div>
  );
}

function DualRow({ label, proyKey, realKey, form, set, proyPlaceholder, realPlaceholder }) {
  return (
    <div className="grid grid-cols-[1fr_1fr_1fr] gap-x-3 items-center mb-2">
      <Label className="text-xs text-slate-600">{label}</Label>
      {proyKey ? (
        <Input type="number" step="0.01" className="h-8 text-xs text-right" placeholder={proyPlaceholder} value={form[proyKey]} onChange={e => set(proyKey, e.target.value)} />
      ) : (
        <Input type="number" step="0.01" className="h-8 text-xs text-right" placeholder={proyPlaceholder} disabled />
      )}
      <Input type="number" step="0.01" className="h-8 text-xs text-right" placeholder={realPlaceholder} value={form[realKey]} onChange={e => set(realKey, e.target.value)} />
    </div>
  );
}
